import { NavLink, useLocation } from "react-router-dom";

import { useAuth } from "@/auth/AuthProvider";
import { canReviewAllFlights, isAdminAccount } from "@/auth/roles";
import { HIDE_REDIS_INSPECTOR } from "@/lib/featureFlags";
import {
  AdminIcon,
  AiIcon,
  AnalysisIcon,
  ChartIcon,
  CoachIcon,
  DatabaseIcon,
  FlightsIcon,
  HomeIcon,
  ListIcon,
  LiveIcon,
  PilotsCompareIcon,
  PilotsIcon,
  PlannerIcon,
  ScenariosIcon,
  TrophyIcon,
  UploadIcon,
} from "./NavIcons";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

function isActivePrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { user } = useAuth();
  const location = useLocation();
  const adminMode = isAdminAccount(user?.account_type);
  const canAccessDirectory = canReviewAllFlights(user?.account_type);

  const flightsActive =
    isActivePrefix(location.pathname, "/flights") ||
    isActivePrefix(location.pathname, "/analysis");
  const pilotsActive = isActivePrefix(location.pathname, "/users");
  const adminActive = isActivePrefix(location.pathname, "/admins");
  const aiActive =
    isActivePrefix(location.pathname, "/ai-coach") ||
    isActivePrefix(location.pathname, "/ai-planner") ||
    isActivePrefix(location.pathname, "/ai-scenarios");

  return (
    <>
      <div
        className={`sidebar-overlay${open ? " active" : ""}`}
        onClick={onClose}
      />
      <aside className={`sidebar${open ? " open" : ""}`}>
        <NavLink className="sidebar-brand" to="/" end>
          <img
            className="brand-mark"
            src="/logo.png"
            alt=""
            width={40}
            height={40}
            draggable={false}
          />
          <span className="brand-text">
            <strong>Flight Analytics</strong>
            <small>By Rankacy</small>
          </span>
        </NavLink>

        <nav className="sidebar-nav">
          <NavLink
            to="/"
            end
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <HomeIcon />
            <span>Home</span>
          </NavLink>

          <NavLink
            to="/performance"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <ChartIcon />
            <span>Performance</span>
          </NavLink>

          <NavLink
            to="/leaderboards"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <TrophyIcon />
            <span>Leaderboards</span>
          </NavLink>

          <div className={`sidebar-nav-group${flightsActive ? " active" : ""}`}>
            <span className="sidebar-nav-group-label">
              <FlightsIcon />
              <span>Flights</span>
            </span>
            <NavLink
              to="/flights"
              end
              className={({ isActive }) =>
                `sidebar-subitem${isActive ? " active" : ""}`
              }
            >
              <ListIcon />
              <span>List</span>
            </NavLink>
            <NavLink
              to="/analysis"
              className={({ isActive }) =>
                `sidebar-subitem${isActive ? " active" : ""}`
              }
            >
              <AnalysisIcon />
              <span>Compare</span>
            </NavLink>
          </div>

          {user && canAccessDirectory && (
            <div className={`sidebar-nav-group${pilotsActive ? " active" : ""}`}>
              <span className="sidebar-nav-group-label">
                <PilotsIcon />
                <span>Pilots</span>
              </span>
              <NavLink
                to="/users"
                end
                className={({ isActive }) =>
                  `sidebar-subitem${isActive ? " active" : ""}`
                }
              >
                <ListIcon />
                <span>List</span>
              </NavLink>
              <NavLink
                to="/users/compare"
                className={({ isActive }) =>
                  `sidebar-subitem${isActive ? " active" : ""}`
                }
              >
                <PilotsCompareIcon />
                <span>Compare</span>
                <span className="sidebar-inline-pill">MOCKUP</span>
              </NavLink>
            </div>
          )}

          {user && adminMode && (
            <div className={`sidebar-nav-group${adminActive ? " active" : ""}`}>
              <span className="sidebar-nav-group-label">
                <AdminIcon />
                <span>Admin</span>
              </span>
              <NavLink
                to="/admins"
                end
                className={({ isActive }) =>
                  `sidebar-subitem${isActive ? " active" : ""}`
                }
              >
                <ListIcon />
                <span>Admins</span>
              </NavLink>
              {!HIDE_REDIS_INSPECTOR ? (
                <NavLink
                  to="/admins/websocket-messages"
                  className={({ isActive }) =>
                    `sidebar-subitem${isActive ? " active" : ""}`
                  }
                >
                  <DatabaseIcon />
                  <span>Redis</span>
                </NavLink>
              ) : null}
            </div>
          )}

          <NavLink
            to="/flights/upload"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <UploadIcon />
            <span>Upload</span>
          </NavLink>

          <div className={`sidebar-nav-group${aiActive ? " active" : ""}`}>
            <span className="sidebar-nav-group-label">
              <AiIcon />
              <span className="sidebar-group-inline">
                <span>AI</span>
                <span className="sidebar-inline-pill">MOCKUP</span>
              </span>
            </span>
            <NavLink
              to="/ai-coach"
              className={({ isActive }) =>
                `sidebar-subitem${isActive ? " active" : ""}`
              }
            >
              <CoachIcon />
              <span>Coach</span>
            </NavLink>
            <NavLink
              to="/ai-planner"
              className={({ isActive }) =>
                `sidebar-subitem${isActive ? " active" : ""}`
              }
            >
              <PlannerIcon />
              <span>Planner</span>
            </NavLink>
            <NavLink
              to="/ai-scenarios"
              className={({ isActive }) =>
                `sidebar-subitem${isActive ? " active" : ""}`
              }
            >
              <ScenariosIcon />
              <span>Scenarios</span>
            </NavLink>
          </div>

          <NavLink
            to="/live"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <LiveIcon />
            <span className="sidebar-group-inline">
              <span>Live Data</span>
              <span className="sidebar-inline-pill">ALPHA</span>
            </span>
          </NavLink>
        </nav>

        <div className="sidebar-foot">
          <div className="sidebar-footer">
            <span className="sidebar-footer__dot" />
            <span>Flight Analytics · v0.1 MVP</span>
          </div>
        </div>
      </aside>
    </>
  );
}
